import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Row,
  Table,
  Col,
  Card,
  CardBody,
  Badge,
} from "reactstrap"
import { useLocation, useNavigate } from "react-router"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import Filters from "components/Common/Filters"
import { useSelector, useDispatch } from "react-redux"
import {
  deleteAdminUser,
  getAllBranchs,
  getAllRoles,
  getAllUsers,
  updateAdminUserStatus,
} from "store/actions/usersAction"
import Switch from "components/Shared/Switch"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import { commonItemPerPage, pageSizes } from "components/Common/constants"
import DownloadCSV from "components/Shared/DownloadCSV"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { useTranslation } from "react-i18next"
import LtrText from "components/Shared/LtrText"
import ConfirmModal from "components/Common/ConfirmModal"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"

const BASE_PATH = "/superadmin/users"

const SuperAdminUsers = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Users List | Super Admin")

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const rolesParam = searchParams.get("roles")
  const dispatch = useDispatch()
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    roles: rolesParam
      ? rolesParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    locations: [],
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })
  const {
    loading,
    isLoading,
    isUpdating,
    totalCount,
    totalPages,
    currentPage,
    usersList,
    usersListStatus,
    branchsList,
    rolesList,
  } = useSelector(state => state.Users)

  const DELETED_USERS_STATUS = 3
  const isActiveUsersListReady =
    !loading &&
    usersListStatus !== null &&
    usersListStatus !== DELETED_USERS_STATUS

  const [itemsPerPage, setItemsPerPage] = useState(commonItemPerPage)
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusUserId, setStatusUserId] = useState(null)
  const [statusToUpdate, setStatusToUpdate] = useState(null)

  const hasRowActions =
    usersPermissions.list ||
    usersPermissions.edit ||
    usersPermissions.delete

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    const isPermissionList = isPermissionEnabled(
      userRole,
      103,
      "sa_users_1",
      accountType,
      accountScope
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      103,
      "sa_users_2",
      accountType,
      accountScope
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      103,
      "sa_users_3",
      accountType,
      accountScope
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      103,
      "sa_users_4",
      accountType,
      accountScope
    )
    if (!isPermissionList) {
      navigateToAccessibleRoute(navigate)
      return
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole, accountType, accountScope, isSuperAdminSession, navigate])

  useEffect(() => {
    fetchUsersData()
    dispatch(getAllBranchs())
    dispatch(getAllRoles())
  }, [])

  const fetchUsersData = () => {
    const { status, roles, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status,
      orderBy: sortOrder,
      keyword: searchTerm,
      role: rolesIds,
      location: [],
    }
    dispatch(getAllUsers(payload))
  }

  const handleDelete = () => {
    const payload = { id: itemIdToDelete, status: "2" }
    dispatch(deleteAdminUser({ ...payload, setShowDeleteModal })).then(action => {
      const { statusCode, status, msg } = action?.payload || {}
      dispatch(showAlert({ text: msg, type: statusCode === 200 && status ? "success" : "error" }))
    })
  }

  const handleConfirmStatusChange = () => {
    if (!statusUserId) return
    const payload = { id: statusUserId, status: statusToUpdate.toString() }
    dispatch(updateAdminUserStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        dispatch(showAlert({ text: msg, type: statusCode === 200 && status ? "success" : "error" }))
      })
      .finally(() => {
        setShowStatusModal(false)
        setStatusUserId(null)
        setStatusToUpdate(null)
      })
  }

  const handleFetchUsers = (page = 1) => {
    const { status, roles, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const params = new URLSearchParams()
    searchTerm && params.set("search", searchTerm)
    params.set("status", status.toString())
    params.set("sortOrder", sortOrder.toString())
    params.set("page", page.toString())
    if (roles.length > 0) {
      params.set("roles", roles.map(item => `${item.label}:${item.value}`).join(","))
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      role: rolesIds,
      keyword: searchTerm || "",
      location: [],
    }

    dispatch(getAllUsers(payload)).then(action => {
      const { statusCode, status: ok } = action?.payload || {}
      if (statusCode === 200 && ok) {
        navigate(`${BASE_PATH}?${params.toString()}`)
      }
    })
  }

  const handleSearch = () => handleFetchUsers(1)
  const onPageChange = page => handleFetchUsers(page)
  const handleSearchChange = e => setSearchTerm(e.target.value)
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prev => ({ ...prev, [filterName]: selectedValue }))
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "roles",
      type: "multiSelect",
      options: rolesList.map(role => ({
        label: language === "sa" ? role.nameAr || "-" : role.name,
        value: role._id,
      })),
      placeholder: t("Select Roles"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const handleReset = () => {
    dispatch(
      getAllUsers({
        perPage: itemsPerPage,
        pageNo: 1,
        status: 2,
        orderBy: 1,
        keyword: "",
        location: [],
      })
    )
    setSelectedFilters({ status: 2, roles: [], locations: [], sortOrder: 1 })
    setSearchTerm("")
    setItemsPerPage(commonItemPerPage)
  }

  const handleExcelDownload = async () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const locationsIds = locations?.map(item => item.value) || []
    const payload = {
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type: "USER",
      exportType: "xlsx",
      role: rolesIds,
      location: locationsIds,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) DownloadXLSX(response, t("Users"))
  }

  const exportToCSV = async () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const locationsIds = locations?.map(item => item.value) || []
    const payload = {
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type: "USER",
      exportType: "csv",
      role: rolesIds,
      location: locationsIds,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) DownloadCSV(response, t("Users"))
  }

  const getUserActionItems = item => [
    ...(usersPermissions.edit
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                {t("Edit")}
              </>
            ),
            onClick: () => navigate(`${BASE_PATH}/edit?id=${item._id}`),
          },
        ]
      : []),
    ...(usersPermissions.list
      ? [
          {
            key: "view",
            content: (
              <>
                <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                {t("View")}
              </>
            ),
            onClick: () => navigate(`${BASE_PATH}/user-details?id=${item._id}`),
          },
        ]
      : []),
    ...(usersPermissions.delete
      ? [
          {
            key: "delete",
            content: (
              <>
                <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                {t("Delete")}
              </>
            ),
            onClick: () => {
              setItemIdToDelete(item._id)
              setShowDeleteModal(true)
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  onDownloadCSV={exportToCSV}
                  onDownloadExcel={handleExcelDownload}
                  cardTitle={t("Users List")}
                  isAddBtn={loading ? false : usersPermissions.create}
                  addBtnText={t("Add New User")}
                  onAddBtnClick={() => navigate(`${BASE_PATH}/add`)}
                  isExportData={loading ? false : true}
                />
                <CardBody>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    isCustomPageSize
                    pageSizes={pageSizes}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    searchBy={t("Name, Email, Phone Number")}
                    colSize={4}
                  />

                  <div>
                    {!isActiveUsersListReady ? (
                      <TableShimmer row={7} col={5} />
                    ) : usersList.length === 0 ? (
                      <p className="text-center my-2">{t("No users found")}</p>
                    ) : (
                      <div className="table-responsive">
                      <Table className="table mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Name")}</th>
                            <th>{t("Email")}</th>
                            <th>{t("Phone Number")}</th>
                            <th>{t("Role")}</th>
                            <th>{t("Status")}</th>
                            <th style={{ minWidth: "fit-content", whiteSpace: "nowrap" }}>{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() =>
                                usersPermissions.list &&
                                navigate(`${BASE_PATH}/user-details?id=${item._id}`)
                              }
                              className={usersPermissions.list ? "cursor-pointer" : undefined}
                            >
                              <th scope="row">{(currentPage - 1) * 10 + index + 1}</th>
                              <td>{item.firstName} {item.lastName}</td>
                              <td>{item.email || "-"}</td>
                              <td>
                                {item.phoneNumber ? (
                                  <LtrText>{item.phoneNumber}</LtrText>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>{item?.role?.name}</td>
                              <td>
                                {item.status === 1 ? (
                                  <Badge className="bg-success">{t("Active")}</Badge>
                                ) : (
                                  <Badge className="bg-danger">{t("Deactive")}</Badge>
                                )}
                              </td>
                              <td>
                                <div className="gap-2 d-flex align-items-center">
                                  {usersPermissions.edit && (
                                    <Switch
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        setStatusUserId(item._id)
                                        setStatusToUpdate(item.status === 1 ? 0 : 1)
                                        setShowStatusModal(true)
                                      }}
                                      isChecked={item.status === 1}
                                      disabled={item._id === selectedId && isUpdating}
                                    />
                                  )}
                                  {hasRowActions && (
                                  <TableActionsDropdown items={getUserActionItems(item)} />
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      </div>
                    )}
                  </div>

                  {isActiveUsersListReady && usersList.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={usersList}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("User"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        isLoading={isUpdating}
        onDeleteClick={handleConfirmStatusChange}
        onCloseClick={() => setShowStatusModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </>
  )
}

export default SuperAdminUsers
